<template>
    <div id="app" :class="[$mq, { 'sidebar-open': !isSidebarCollapsed && isMobileView }]">
        <template v-if="!isMaintenanceOn">
            <Notification />
            <Header />
            <Sidebar v-if="isMobileView" />
            <router-view :key="$route.path" class="app-content" :style="cssVars" v-if="isTotemStarted"/>
            <FooterGoLearn v-if="language === 'dk'" />
            <FooterEduhouse v-else />
            <v-dialog />
            <Loader/>
            <TotemToast ref="toast"/>
            <AlertBox v-if="showAlertBox" />
            <template v-if="isNewChatUIEnabled && isAllUserDataLoaded && language !== 'dk'">
                <HubSpotChatOnly v-if="!hasAccessToChatBot"/>
                <ChatComboButton v-if="hasAccessToChatBot" />
                <ChatComboModal v-if="hasAccessToChatBot" />
            </template>
            <template v-else-if="!isNewChatUIEnabled && isAllUserDataLoaded && language !== 'dk'">
                <HubspotDisabledPopupOld v-if="!isHubspotChatAvailable" />
                <ShowChatBotButtonOld v-if="!showChatBot && hasAccessToChatBot" />
                <EvaChatBotOld v-if="hasAccessToChatBot"/>
            </template>
            <ModalFindingContent />
            <ModalTransitionalPeriod />
            <ModalEvaGeneralNorway />
            <ModalEvaGeneralSweden />
            <AddToWatchListModal />
        </template>
        <template v-else>
            <PageUnderMaintenance />
        </template>
    </div>
</template>

<script>
    import Header from 'components/layout/Header'
    import Sidebar from 'components/layout/Sidebar'
    import FooterEduhouse from 'components/layout/FooterEduhouse'
    import FooterGoLearn from 'components/layout/FooterGoLearn'
    import Notification from 'components/layout/Notification'
    import loadingOverlay from 'components/shared/mixins/loadingOverlay'
    import { mapGetters } from 'vuex'
    import Loader from 'components/layout/Loader'
    import { loadRemoteLocaleMessages } from 'services/i18n'
    import PageUnderMaintenance from './views/PageUnderMaintenance.vue'
    import dateHelper from 'components/shared/mixins/dateHelper'
    import AlertBox from './components/layout/Alert'
    import cookieConsentHelper from 'components/shared/mixins/cookieConsentHelper'
    import ModalEvaGeneralNorway from './components/shared/modals/ModalEvaGeneralNorway'
    import ModalEvaGeneralSweden from './components/shared/modals/ModalEvaGeneralSweden'
    import featureModalHelper from 'components/shared/mixins/featureModalHelper'
    import HubSpotChatOnly from 'components/chatbot/customer_support/HubSpotChatOnly.vue'
    import ChatComboButton from 'components/chatbot/ChatComboButton.vue'
    import ChatComboModal from 'components/chatbot/ChatComboModal.vue'
    import EvaChatBotOld from 'components/chatbot/assistant/EvaChatBotOld.vue'
    import ShowChatBotButtonOld from 'components/chatbot/assistant/ShowChatBotButtonOld.vue'
    import HubspotDisabledPopupOld from 'components/cookieconsent/HubspotDisabledPopupOld.vue'
    import ModalFindingContent from './components/shared/modals/ModalFindingContent'
    import ModalTransitionalPeriod from './components/shared/modals/ModalTransitionalPeriod'

    import config from 'services/config'
    import AddToWatchListModal from 'components/shared/global-courses-items/totem-add-to-watchlist-modal'

    export default {
        components: {
            HubspotDisabledPopupOld,
            EvaChatBotOld,
            ShowChatBotButtonOld,
            ChatComboModal,
            HubSpotChatOnly,
            ChatComboButton,
            AlertBox,
            Loader,
            Header,
            Sidebar,
            FooterEduhouse,
            FooterGoLearn,
            Notification,
            PageUnderMaintenance,
            AddToWatchListModal,
            ModalEvaGeneralNorway,
            ModalEvaGeneralSweden,
            ModalFindingContent,
            ModalTransitionalPeriod
        },
        mixins: [ loadingOverlay, dateHelper, cookieConsentHelper, featureModalHelper ],
        provide () {
            return {
                headerTheme: () => this.theme
            }
        },
        metaInfo () {
            return {
                title: config.appLanguage === 'dk' ? 'GoLearn' : 'Eduhouse',
                meta: [{
                    vmid: 'viewport',
                    name: 'viewport',
                    content: 'width=device-width, initial-scale=1, minimum-scale=1'
                }],
                htmlAttrs: {
                    lang: this.language
                },
                link: [
                    { rel: 'canonical', href: `https://app.eduhouse.${this.$totemLang}` + this.computedRoute }
                ]
            }
        },
        data () {
            return {
                showHubspotDisabledPopup: false,
                scrollPosition: null,
                isNewChatUIEnabled: config.chatBotNewUIEnabled,
            }
        },
        computed: {
            ...mapGetters({
                isLoggedIn: 'isLoggedIn',
                language: 'getLanguage',
                isTotemStarted: 'isTotemStarted',
                showNotification: 'isNotificationVisible',
                isSidebarCollapsed: 'getIsSidebarCollapsed',
                isMaintenanceOn: 'getMaintenanceState',
                showAlertBox: 'getAlertMessage',
                showChatBot: 'ChatBot/getShowChatBot',
                getCurrentUser: 'User/getCurrentUser',
                getUserInfo: 'User/getUserInfo',
                isReportingAllowed: 'User/getIsReportingAllowed',
                getNotificationHeight: 'getNotificationHeight',
                isAllUserDataLoaded: 'User/isAllDataLoaded',
                isHubspotChatAvailable: 'Hubspot/hubspotChatIsAvailable',
                searchQuery: 'Search/searchQuery'
            }),
            language () {
                if (this.$totemLang === 'se') {
                    return 'sv'
                }
                return this.$totemLang
            },
            computedRoute () {
                if (this.$route.params.packageSlug === undefined) {
                    return this.$route.path
                }
                if (this.$route.params.packageSlug !== undefined) {
                    let pathArray = this.$route.path.split('/')
                    if (this.$route.name === 'course-list-course-page') {
                        pathArray.splice(2, 1)
                    }
                    if (this.$route.name === 'collection-page-course-page') {
                        pathArray.splice(2, 2)
                        pathArray[2] = this.$t('Url.courses')
                    }
                    return pathArray.join('/')
                }
                return ''
            },
            hasAccessToChatBot () {
                return this.isLoggedIn && this.getUserInfo && this.getUserInfo.hasAccessToChat
            },
            isLandingPage () {
                return this.$route.name === 'home' || this.$route.name === 'home-courses'
            },
            isLibraryViewWithMainPackage () {
                return this.$route.name === 'all-content' && !!this.$route.params?.packageSlug && (this.searchQuery === '' || this.searchQuery === null)
            },
            theme () {
                let scrollPosition = 80

                if (this.getNotificationHeight !== 0) {
                    scrollPosition = 30
                }

                if ((this.isLandingPage || this.isLibraryViewWithMainPackage) && this.scrollPosition < scrollPosition) {
                    return 'dark'
                }

                return 'light'
            },
            cssVars () {
                if (this.isMobileView) {
                    return { '--padding-top': '64px' }
                }

                if (this.getNotificationHeight === 0) {
                    return { '--padding-top': '80px' }
                }

                if (this.isLandingPage || this.isLibraryViewWithMainPackage) {
                    return { '--padding-top': 0 }
                }

                return { '--padding-top': 80 + this.getNotificationHeight + 'px' }
            }
        },
        methods: {
            showToast (text) {
                this.$refs.toast.show(text)
            },
            updateScroll () {
                this.scrollPosition = window.scrollY
            },
            createHubSpotEmbedScript (loadImmediately) {
                window.hsConversationsSettings = {
                    loadImmediately: loadImmediately,
                }
                const hsWidget = document.createElement('script')
                hsWidget.src = `https://js.hs-scripts.com/${config.hubSpotPortalId}.js`
                hsWidget.async = true
                hsWidget.defer = true
                document.head.appendChild(hsWidget)
            },
        },
        watch: {
            isLoggedIn: function (newState, oldState) {
                if (newState) {
                    this.showToast(this.$t('Login.logged_in'))
                    this.showFindingContentModal()
                } else {
                    this.showToast(this.$t('Login.logged_out'))
                    if(window.HubSpotConversations) {
                        window.HubSpotConversations.clear()
                    }
                }
            }
        },
        created () {
            const isSSOLogin = this.$route.path.includes('/palvelu/kirjaudu/sso/callback/')
            this.$store.dispatch('startTotem', isSSOLogin)
        },
        mounted () {
            window.addEventListener('scroll', this.updateScroll)
            if (this.isNewChatUIEnabled) {
                this.createHubSpotEmbedScript(false)
            } else {
                this.createHubSpotEmbedScript(true)
            }

            if (!this.isLoggedIn) {
                this.showTransitionalPeriodModal()
            }
        }
    }// Comment for triggering fresh build
</script>

<style lang="scss" scoped>
    #app {
        &.sidebar-open {
            position: fixed;
        }
    }

    .app-content {
        transition: 0.15s padding-left ease-out;
        padding-left: 0;
        padding-top: var(--padding-top);
    }
</style>

<style>
    a {
        text-decoration: none;
        color: #000;
    }
</style>
